import React, { useState, useEffect } from 'react';
import { useAuth } from '../store';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

interface Branch {
  id: string;
  name: string;
}

interface NotificationSettings {
  id: string;
  branch_id: string;
  daily_summary_enabled: boolean;
  daily_summary_time: string;
  alert_recipients: string[];
  stock_alert_threshold: number;
  cc_email: string;
}

const NotificationSettingsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!['super_admin', 'finance', 'accounting'].includes(user?.role || '')) {
      navigate('/dashboard');
      return;
    }
    fetchBranches();
  }, [user, navigate]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, { headers });
      const branchList = res.data.data || res.data || [];
      setBranches(branchList);
      if (branchList.length > 0) {
        setSelectedBranchId(branchList[0].id);
        fetchSettings(branchList[0].id);
      }
    } catch (err: any) {
      setError('Gagal mengambil daftar cabang');
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (branchId: string) => {
    setSelectedBranchId(branchId);
    fetchSettings(branchId);
  }

  const fetchSettings = async (branchId: string) => {
    try {
      const res = await axios.get(`${API}/admin/notification-settings/${branchId}`, { headers });
      setSettings(res.data.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal mengambil pengaturan');
    }
  };

  const handleSave = async () => {
    if (!settings || !selectedBranchId) return;
    try {
      setSaving(true);
      await axios.post(`${API}/admin/notification-settings/${selectedBranchId}`, {
        daily_summary_enabled: settings.daily_summary_enabled,
        daily_summary_time: settings.daily_summary_time,
        alert_recipients: settings.alert_recipients,
        stock_alert_threshold: settings.stock_alert_threshold,
      }, { headers });
      alert('Pengaturan berhasil disimpan!');
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!selectedBranchId) return;
    try {
      setSending(true);
      const res = await axios.post(`${API}/admin/notification-settings/${selectedBranchId}/send-now`, {}, { headers });
      alert(`✅ ${res.data.message}`);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal mengirim laporan');
    } finally {
      setSending(false);
    }
  };

  const addEmail = () => {
    if (!newEmail.trim()) return;
    if (settings && !settings.alert_recipients.includes(newEmail)) {
      setSettings({
        ...settings,
        alert_recipients: [...settings.alert_recipients, newEmail],
      });
      setNewEmail('');
    }
  };

  const removeEmail = (email: string) => {
    if (settings) {
      setSettings({
        ...settings,
        alert_recipients: settings.alert_recipients.filter(e => e !== email),
      });
    }
  };

  const handleTimeChange = (newTime: string) => {
    if (settings) {
      setSettings({ ...settings, daily_summary_time: newTime });
    }
  };

  const toggleDailySummary = () => {
    if (settings) {
      setSettings({ ...settings, daily_summary_enabled: !settings.daily_summary_enabled });
    }
  };

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}>Memuat...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <h1 style={{ marginBottom: 24, color: '#1e293b' }}>⚙️ Pengaturan Notifikasi Global</h1>

      {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', padding: 12, borderRadius: 6, marginBottom: 20 }}>{error}</div>}

      {/* Branch Selector */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
          Pilih Cabang
        </label>
        <select
          value={selectedBranchId}
          onChange={(e) => handleBranchChange(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            fontSize: 14,
            maxWidth: 400,
          }}
        >
          {branches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {!settings && selectedBranchId && <div style={{ padding: 24, textAlign: 'center' }}>Pengaturan tidak ditemukan</div>}
      {settings && selectedBranchId && (
        <div>

      {/* Daily Summary Setting */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>📊 Laporan Stok Harian</h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Email ringkasan stok gudang & chiller setiap hari</p>
          </div>
          <button
            onClick={toggleDailySummary}
            style={{
              padding: '8px 16px',
              background: settings.daily_summary_enabled ? '#10b981' : '#e2e8f0',
              color: settings.daily_summary_enabled ? '#fff' : '#64748b',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {settings.daily_summary_enabled ? '✅ Aktif' : '⊘ Nonaktif'}
          </button>
        </div>

        {settings.daily_summary_enabled && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                  Waktu Pengiriman
                </label>
                <input
                  type="time"
                  value={settings.daily_summary_time}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14,
                  }}
                />
                <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>Format: HH:MM (WIB)</p>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                  Batas Alert Stok (pcs)
                </label>
                <input
                  type="number"
                  value={settings.stock_alert_threshold}
                  onChange={(e) => setSettings({ ...settings, stock_alert_threshold: parseInt(e.target.value) || 5 })}
                  min="1"
                  max="100"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14,
                  }}
                />
                <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>Trigger alert jika stok ≤ nilai ini</p>
              </div>
            </div>
            <button
              onClick={handleSendNow}
              disabled={sending}
              style={{
                padding: '10px 16px',
                background: sending ? '#cbd5e1' : '#0284c7',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: sending ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {sending ? 'Mengirim...' : '📤 Kirim Laporan Sekarang'}
            </button>
          </div>
        )}
      </div>

      {/* Alert Recipients */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 16px' }}>📧 Penerima Alert</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
            Email Alert (selain CC info@brosco.id)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="nama@example.com"
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                fontSize: 14,
              }}
            />
            <button
              onClick={addEmail}
              style={{
                padding: '10px 16px',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              + Tambah
            </button>
          </div>
        </div>

        {/* Recipients List */}
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Daftar Email:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* CC Email (readonly) */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              background: '#f0f9ff',
              border: '1px solid #0284c7',
              borderRadius: 6,
            }}>
              <span style={{ fontSize: 14, color: '#1e293b' }}>{settings.cc_email}</span>
              <span style={{ fontSize: 11, color: '#0284c7', fontWeight: 700 }}>CC (Pusat)</span>
            </div>

            {/* Dynamic Emails */}
            {settings.alert_recipients.map((email) => (
              <div key={email} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
              }}>
                <span style={{ fontSize: 14, color: '#1e293b' }}>{email}</span>
                <button
                  onClick={() => removeEmail(email)}
                  style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Hapus
                </button>
              </div>
            ))}

            {settings.alert_recipients.length === 0 && (
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Belum ada email tambahan</p>
            )}
          </div>
        </div>
      </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: saving ? '#cbd5e1' : '#10b981',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {saving ? 'Menyimpan...' : '💾 Simpan Pengaturan'}
        </button>
        </div>
      )}
    </div>
  );
};

export default NotificationSettingsPage;
